/**
 * Institution leader hub: teachers manage classes (create, join code, add by email / pool);
 * KPIs and roster come from live enrollments — empty until a teacher adds students.
 * LTI launches sync LMS course context into ClassSections.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import Alert from '@mui/material/Alert';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SchoolIcon from '@mui/icons-material/School';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import defaultMetrics from '../data/leaderDashboardMockMetrics.json';
import { getLearningDashboardUrl } from '../lib/learningDashboardUrl.js';
import { useLanguage } from '../contexts/LanguageContext.js';
import TeacherGradebookPanel from './TeacherGradebookPanel.js';
import TeacherPackMarketplace from './TeacherPackMarketplace.js';
import HelpTip, { TEACHER_KPI_HELP } from './HelpTip.js';

const hubTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#22d3ee' },
    secondary: { main: '#0d9488' },
    background: { default: '#040810', paper: '#121c2f' },
    text: { primary: '#e8f4ff', secondary: '#94a3b8' }
  },
  typography: {
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(165deg, rgba(16, 28, 52, 0.95), rgba(8, 14, 28, 0.98))',
          border: '1px solid rgba(120, 200, 255, 0.22)'
        }
      }
    }
  }
});

function deepMerge(a, b) {
  if (b == null) return a;
  if (Array.isArray(b)) return b;
  if (typeof b !== 'object') return b;
  const out = { ...a };
  for (const k of Object.keys(b)) {
    const bv = b[k];
    const av = a[k];
    if (bv && typeof bv === 'object' && !Array.isArray(bv) && av && typeof av === 'object' && !Array.isArray(av)) {
      out[k] = deepMerge(av, bv);
    } else {
      if (bv === null || bv === undefined) continue;
      out[k] = bv;
    }
  }
  return out;
}

function normalizeLeaderMetrics(merged, base) {
  const textBlockOk = (v) => v && typeof v === 'object' && typeof v.title === 'string';
  const lmsOk = (v) => v && typeof v === 'object' && typeof v.title === 'string' && typeof v.body === 'string';
  return {
    ...merged,
    kpis: Array.isArray(merged.kpis) ? merged.kpis : [],
    barChart: merged.barChart && typeof merged.barChart === 'object' ? merged.barChart : base.barChart,
    donut: merged.donut && typeof merged.donut === 'object' ? merged.donut : base.donut,
    bubbles: merged.bubbles && typeof merged.bubbles === 'object' ? merged.bubbles : base.bubbles,
    horizontalBars:
      merged.horizontalBars && typeof merged.horizontalBars === 'object'
        ? merged.horizontalBars
        : base.horizontalBars,
    forecast: textBlockOk(merged.forecast) ? merged.forecast : base.forecast,
    license: textBlockOk(merged.license) ? merged.license : base.license,
    lmsPlaceholder: lmsOk(merged.lmsPlaceholder) ? merged.lmsPlaceholder : base.lmsPlaceholder
  };
}

function Sparkline({ values, color }) {
  const w = 120;
  const h = 28;
  if (!values?.length) return null;
  if (values.length === 1) {
    return (
      <svg width={w} height={h} style={{ display: 'block', opacity: 0.95 }}>
        <circle cx={w / 2} cy={h / 2} r={3} fill={color} />
      </svg>
    );
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * (w - 4) + 2;
      const y = h - 4 - ((v - min) / span) * (h - 8);
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', opacity: 0.95 }}>
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts} />
    </svg>
  );
}

function Gauge({ current, max, color }) {
  const pct = Math.max(0, Math.min(100, (Number(current) / Math.max(Number(max) || 1, 1)) * 100));
  return (
    <Box sx={{ mt: 0.5, height: 6, bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: color, borderRadius: 1 }} />
    </Box>
  );
}

function BarChartBlock({ data }) {
  if (!data?.categories?.length) {
    return (
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2, minHeight: 200 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: '#e8f4ff' }}>
          {data?.title || 'No class data yet'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
          Create a class and add students to see completion by section.
        </Typography>
      </Paper>
    );
  }
  const max = Math.max(...data.primary, ...(data.secondary || []), 1);
  const barW = 36;
  const gap = 24;
  const chartH = 180;
  const n = data.categories.length;
  const totalW = n * (barW * 2 + gap) + 40;
  return (
    <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5, color: '#e8f4ff' }}>
        {data.title}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <svg width={totalW} height={chartH + 48} role="img" aria-label="Completion by class">
          {data.categories.map((cat, i) => {
            const x0 = 32 + i * (barW * 2 + gap);
            const h1 = (data.primary[i] / max) * chartH;
            return (
              <g key={cat}>
                <rect x={x0} y={chartH - h1 + 8} width={barW * 1.4} height={h1} fill="#0d9488" rx={4} />
                <text x={x0 + barW * 0.7} y={chartH + 28} fontSize="11" fill="#94a3b8" textAnchor="middle">
                  {cat.length > 14 ? `${cat.slice(0, 12)}…` : cat}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
      <Typography variant="caption" sx={{ color: '#94a3b8' }}>
        {data.legendPrimary || 'Average pathway completion %'}
      </Typography>
    </Paper>
  );
}

function formatLicenseExpirationDate(endsAt) {
  if (!endsAt) return null;
  const d = new Date(endsAt);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day} / ${month} / ${year}`;
}

function progressMeterStyle(pct) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  if (clamped >= 80) {
    return {
      fill: 'linear-gradient(90deg, #047857 0%, #10b981 55%, #6ee7b7 100%)',
      chip: '#065f46',
      chipBg: 'rgba(16,185,129,0.14)'
    };
  }
  if (clamped >= 60) {
    return {
      fill: 'linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #99f6e4 100%)',
      chip: '#115e59',
      chipBg: 'rgba(20,184,166,0.16)'
    };
  }
  return {
    fill: 'linear-gradient(90deg, #c2410c 0%, #fb923c 72%, #fdba74 100%)',
    chip: '#9a3412',
    chipBg: 'rgba(251,146,60,0.18)'
  };
}

function TeacherStudentProgressRow({ name, email, pct, onRemove }) {
  const style = progressMeterStyle(pct);
  return (
    <Box sx={{ mb: 1.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4, gap: 1 }}>
        <Box sx={{ minWidth: 0 }} className="skylite-privacy-blur" data-skylite-privacy="blur">
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#e8f4ff', lineHeight: 1.25 }}>
            {name}
          </Typography>
          {email ? (
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
              {email}
            </Typography>
          ) : null}
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Chip
            label={`${pct}%`}
            size="small"
            sx={{ height: 22, fontWeight: 800, color: style.chip, bgcolor: style.chipBg }}
          />
          {onRemove ? (
            <Button size="small" color="inherit" onClick={onRemove} sx={{ textTransform: 'none', minWidth: 0, px: 0.5 }}>
              Remove
            </Button>
          ) : null}
        </Stack>
      </Box>
      <Box
        sx={{
          height: 10,
          borderRadius: 1,
          bgcolor: 'rgba(148,163,184,0.2)',
          overflow: 'hidden',
          backgroundImage:
            'repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(148,163,184,0.35) 7px, rgba(148,163,184,0.35) 8px)'
        }}
      >
        <Box sx={{ width: `${pct}%`, height: '100%', background: style.fill }} />
      </Box>
    </Box>
  );
}

export default function InstitutionLeaderDashboard({
  sessionRole,
  institutionName,
  previewUser,
  onEnterSkylite,
  onExitSupportPreview,
  canImpersonateStudents = false
}) {
  const { t } = useLanguage();
  const baseMetrics = useMemo(() => {
    const base = JSON.parse(JSON.stringify(defaultMetrics));
    const merged = previewUser?.metricsPatch ? deepMerge(base, previewUser.metricsPatch) : base;
    return normalizeLeaderMetrics(merged, base);
  }, [previewUser]);

  const role = String(sessionRole || '').toLowerCase();
  const isTeacherHub = role === 'teacher';
  const isDistrictLeaderHub = ['admin', 'tester', 'principal', 'superintendent'].includes(role);
  const isStudentHub = role === 'student';
  const canManageClasses = isTeacherHub || role === 'admin' || role === 'tester';
  const showSkyliteCard = role === 'admin' || role === 'tester';
  /** Support mimic: do not mutate live school data under the owner session. */
  const supportPreviewReadOnly = Boolean(previewUser);
  const supportPreviewCanLoadLive = Boolean(previewUser?.institutionId);
  /** Legacy name used in UI: mutations blocked in support preview. */
  const livePreviewBlocked = supportPreviewReadOnly;

  const [teacherSection, setTeacherSection] = useState('all');
  const [sections, setSections] = useState([]);
  const [rosterStudents, setRosterStudents] = useState([]);
  const [liveKpis, setLiveKpis] = useState([]);
  const [barChartData, setBarChartData] = useState(baseMetrics.barChart);
  const [unassigned, setUnassigned] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [classError, setClassError] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [licenseEndsAt, setLicenseEndsAt] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [showRenewalPrompt, setShowRenewalPrompt] = useState(false);
  const [renewalInquirySent, setRenewalInquirySent] = useState(false);
  const [renewalBusy, setRenewalBusy] = useState(false);
  const [renewalError, setRenewalError] = useState('');
  const [resolvedNotices, setResolvedNotices] = useState([]);
  const [resolvedNoticesAnchor, setResolvedNoticesAnchor] = useState(null);
  const [dismissingNoticeId, setDismissingNoticeId] = useState(null);
  const [gradebookFocus, setGradebookFocus] = useState(null);
  const [kpiHint, setKpiHint] = useState('');
  const [marketplaceOpen, setMarketplaceOpen] = useState(false);
  const [enabledTrainingPacks, setEnabledTrainingPacks] = useState([]);
  const gradebookPanelRef = useRef(null);

  useEffect(() => {
    const closeMarketplace = () => setMarketplaceOpen(false);
    window.addEventListener('commandline-close-marketplace', closeMarketplace);
    return () => window.removeEventListener('commandline-close-marketplace', closeMarketplace);
  }, []);

  useEffect(() => {
    if (previewUser) {
      setLicenseEndsAt(null);
      setShowRenewalPrompt(false);
      setRenewalInquirySent(false);
      setLicenseLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLicenseLoading(true);
      try {
        const response = await fetch('/api/institutions/active/license-summary', { credentials: 'include' });
        const payload = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok) {
          setLicenseEndsAt(payload?.license?.endsAt || null);
          setShowRenewalPrompt(Boolean(payload?.renewal?.showRenewalPrompt));
          setRenewalInquirySent(Boolean(payload?.renewal?.renewalInquirySent));
          if (Array.isArray(payload?.enabledTrainingPacks)) {
            setEnabledTrainingPacks(payload.enabledTrainingPacks);
          }
        } else {
          setLicenseEndsAt(null);
          setShowRenewalPrompt(false);
          setRenewalInquirySent(false);
          setEnabledTrainingPacks([]);
        }
      } catch {
        if (!cancelled) {
          setLicenseEndsAt(null);
          setShowRenewalPrompt(false);
          setRenewalInquirySent(false);
        }
      } finally {
        if (!cancelled) setLicenseLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [previewUser]);

  const refreshResolvedNotices = useCallback(async () => {
    if (previewUser || (!isTeacherHub && role !== 'admin' && role !== 'tester')) {
      setResolvedNotices([]);
      return;
    }
    try {
      const res = await fetch('/api/institutions/active/resolved-ticket-notices', {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) {
        setResolvedNotices([]);
        return;
      }
      const json = await res.json().catch(() => ({}));
      setResolvedNotices(Array.isArray(json.items) ? json.items : []);
    } catch {
      setResolvedNotices([]);
    }
  }, [previewUser, isTeacherHub, role]);

  useEffect(() => {
    refreshResolvedNotices();
    if (previewUser) return undefined;
    const timer = setInterval(refreshResolvedNotices, 60000);
    return () => clearInterval(timer);
  }, [refreshResolvedNotices, previewUser]);

  const dismissResolvedNotice = useCallback(
    async (noticeId) => {
      if (!noticeId || dismissingNoticeId) return;
      setDismissingNoticeId(noticeId);
      try {
        const res = await fetch(`/api/institutions/active/resolved-ticket-notices/${noticeId}/dismiss`, {
          method: 'POST',
          credentials: 'include',
          headers: { Accept: 'application/json' }
        });
        if (res.ok) {
          setResolvedNotices((prev) => prev.filter((n) => Number(n.id) !== Number(noticeId)));
        }
      } finally {
        setDismissingNoticeId(null);
      }
    },
    [dismissingNoticeId]
  );

  const handleRenewalInquiry = useCallback(async () => {
    if (renewalInquirySent || renewalBusy) return;
    setRenewalError('');
    setRenewalBusy(true);
    try {
      const response = await fetch('/api/institutions/active/license-renewal-inquiry', {
        method: 'POST',
        credentials: 'include',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });
      const payload = await response.json().catch(() => ({}));
      if (response.status === 409 || payload?.renewalInquirySent) {
        setRenewalInquirySent(true);
        return;
      }
      if (!response.ok) {
        setRenewalError(
          payload?.error === 'smtp_not_configured'
            ? 'Renewal email is not configured yet. Please contact info@enterceptor.com directly.'
            : 'Could not send renewal request. Please try again or contact info@enterceptor.com.'
        );
        return;
      }
      setRenewalInquirySent(true);
    } catch {
      setRenewalError('Could not send renewal request. Please try again or contact info@enterceptor.com.');
    } finally {
      setRenewalBusy(false);
    }
  }, [renewalBusy, renewalInquirySent]);

  const licenseExpirationFormatted = useMemo(
    () => formatLicenseExpirationDate(licenseEndsAt),
    [licenseEndsAt]
  );

  const refreshClasses = useCallback(async () => {
    if (supportPreviewCanLoadLive) {
      setLoadingClasses(true);
      setClassError('');
      try {
        const params = new URLSearchParams();
        params.set('institutionId', String(previewUser.institutionId));
        params.set('role', String(previewUser.roleAtInstitution || 'teacher'));
        params.set('sectionId', teacherSection || 'all');
        if (previewUser.teacherUserId != null) {
          params.set('teacherUserId', String(previewUser.teacherUserId));
        }
        if (previewUser.email) {
          params.set('teacherEmail', String(previewUser.email));
        }
        const res = await fetch(`/api/admin/owner-support/class-hub?${params.toString()}`, {
          credentials: 'include',
          headers: { Accept: 'application/json' }
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setSections([]);
          setRosterStudents([]);
          setLiveKpis([]);
          setBarChartData(baseMetrics.barChart);
          setUnassigned([]);
          setClassError(
            json.error === 'teacher_user_not_found'
              ? 'Could not match that instructor email to an active teacher account at this school.'
              : json.error
                ? String(json.error).replace(/_/g, ' ')
                : 'Could not load live classes for this preview.'
          );
          return;
        }
        setSections(Array.isArray(json.sections) ? json.sections : []);
        setRosterStudents(Array.isArray(json.students) ? json.students : []);
        setLiveKpis(Array.isArray(json.kpis) ? json.kpis : []);
        if (json.barChart) setBarChartData(json.barChart);
        else setBarChartData(baseMetrics.barChart);
        setUnassigned(Array.isArray(json.unassigned) ? json.unassigned : []);
      } catch (err) {
        console.error(err);
        setClassError('Network error loading live class preview.');
        setSections([]);
        setRosterStudents([]);
        setLiveKpis([]);
        setBarChartData(baseMetrics.barChart);
        setUnassigned([]);
      } finally {
        setLoadingClasses(false);
      }
      return;
    }
    if (!canManageClasses || supportPreviewReadOnly) {
      setSections([]);
      setRosterStudents([]);
      setLiveKpis([]);
      setBarChartData(baseMetrics.barChart);
      setUnassigned([]);
      return;
    }
    setLoadingClasses(true);
    setClassError('');
    try {
      const [secRes, rosterRes, poolRes] = await Promise.all([
        fetch('/api/institutions/active/sections', { credentials: 'include' }),
        fetch(
          `/api/institutions/active/sections/roster?sectionId=${encodeURIComponent(teacherSection || 'all')}`,
          { credentials: 'include' }
        ),
        fetch('/api/institutions/active/students/unassigned', { credentials: 'include' })
      ]);
      if (!secRes.ok) throw new Error('sections_failed');
      const secJson = await secRes.json();
      setSections(Array.isArray(secJson.sections) ? secJson.sections : []);

      if (rosterRes.ok) {
        const rosterJson = await rosterRes.json();
        setRosterStudents(Array.isArray(rosterJson.students) ? rosterJson.students : []);
        setLiveKpis(Array.isArray(rosterJson.kpis) ? rosterJson.kpis : []);
        if (rosterJson.barChart) setBarChartData(rosterJson.barChart);
      } else {
        setRosterStudents([]);
        setLiveKpis([]);
      }

      if (poolRes.ok) {
        const poolJson = await poolRes.json();
        setUnassigned(Array.isArray(poolJson.students) ? poolJson.students : []);
      } else {
        setUnassigned([]);
      }
    } catch (err) {
      console.error(err);
      setClassError('Could not load classes. Confirm you have an active school membership.');
      setSections([]);
      setRosterStudents([]);
      setLiveKpis([]);
    } finally {
      setLoadingClasses(false);
    }
  }, [
    canManageClasses,
    supportPreviewReadOnly,
    supportPreviewCanLoadLive,
    previewUser,
    teacherSection,
    baseMetrics.barChart
  ]);

  useEffect(() => {
    if (!canManageClasses || (supportPreviewReadOnly && !supportPreviewCanLoadLive) || loadingClasses) {
      return undefined;
    }
    // Do not re-apply a stored non-English preference over teacher/owner English chrome.
    if (window.__skyliteForceEnglishChrome) return undefined;
    const mgr = window.__skyliteAutoTranslateManager;
    if (mgr && typeof mgr.isSuspended === 'function' && mgr.isSuspended()) return undefined;
    const t = window.setTimeout(() => {
      if (typeof window.__skyliteRequestRetranslate === 'function') {
        window.__skyliteRequestRetranslate({ force: true });
      }
    }, 150);
    return () => window.clearTimeout(t);
  }, [canManageClasses, supportPreviewReadOnly, supportPreviewCanLoadLive, loadingClasses, sections, rosterStudents]);

  useEffect(() => {
    refreshClasses();
  }, [refreshClasses]);

  useEffect(() => {
    if (teacherSection === 'all' || loadingClasses) return;
    const stillThere = sections.some((s) => String(s.id) === String(teacherSection));
    if (!stillThere) setTeacherSection('all');
  }, [sections, teacherSection, loadingClasses]);

  const displayKpis = useMemo(() => {
    if (canManageClasses && (!supportPreviewReadOnly || supportPreviewCanLoadLive)) return liveKpis;
    return [];
  }, [canManageClasses, supportPreviewReadOnly, supportPreviewCanLoadLive, liveKpis]);

  const showLiveClassPanels = canManageClasses && (!supportPreviewReadOnly || supportPreviewCanLoadLive);

  const selectedSection = useMemo(
    () => sections.find((s) => String(s.id) === String(teacherSection)) || null,
    [sections, teacherSection]
  );

  const audience = useMemo(() => {
    if (previewUser) {
      return `Support preview · ${previewUser.name} (${previewUser.roleAtInstitution})`;
    }
    if (isTeacherHub) return 'Teacher workspace · manage your classes';
    if (role === 'tester') return 'Tester · institution board';
    if (isDistrictLeaderHub) return 'Principal / superintendent · school overview';
    return 'Institution workspace';
  }, [previewUser, isTeacherHub, isDistrictLeaderHub, role]);

  const displayInstitutionName = institutionName;
  const trainingUrl = getLearningDashboardUrl();

  async function createSection() {
    const name = newSectionName.trim();
    if (!name || busy) return;
    setBusy(true);
    setClassError('');
    try {
      const res = await fetch('/api/institutions/active/sections', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error('create_failed');
      setNewSectionName('');
      await refreshClasses();
    } catch {
      setClassError('Could not create class.');
    } finally {
      setBusy(false);
    }
  }

  async function addStudentByEmail() {
    const email = addEmail.trim();
    if (!email || !selectedSection || busy) return;
    setBusy(true);
    setClassError('');
    try {
      const res = await fetch(`/api/institutions/active/sections/${selectedSection.id}/members`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'manual_email' })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'add_failed');
      setAddEmail('');
      await refreshClasses();
    } catch (err) {
      setClassError(err.message === 'user_not_found' ? 'No account found for that email.' : 'Could not add student.');
    } finally {
      setBusy(false);
    }
  }

  async function addFromPool(userId) {
    if (!selectedSection || busy) return;
    setBusy(true);
    setClassError('');
    try {
      const res = await fetch(`/api/institutions/active/sections/${selectedSection.id}/members`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, source: 'unassigned_pool' })
      });
      if (!res.ok) throw new Error('pool_failed');
      await refreshClasses();
    } catch {
      setClassError('Could not add student from pool.');
    } finally {
      setBusy(false);
    }
  }

  async function removeStudent(userId) {
    if (!selectedSection || busy) return;
    setBusy(true);
    try {
      await fetch(
        `/api/institutions/active/sections/${selectedSection.id}/members/${userId}/remove`,
        { method: 'POST', credentials: 'include' }
      );
      await refreshClasses();
    } catch {
      setClassError('Could not remove student.');
    } finally {
      setBusy(false);
    }
  }

  async function archiveSection() {
    if (!selectedSection || busy) return;
    setBusy(true);
    try {
      await fetch(`/api/institutions/active/sections/${selectedSection.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' })
      });
      setTeacherSection('all');
      await refreshClasses();
    } catch {
      setClassError('Could not archive class.');
    } finally {
      setBusy(false);
    }
  }

  function openGradebookFromKpi(kpiId) {
    const mode =
      kpiId === 'on_track' ? 'on_track' : kpiId === 'needs_help' || kpiId === 'radar' ? 'radar' : null;
    if (!mode) return;
    if (teacherSection === 'all') {
      if (sections.length === 1) {
        setTeacherSection(String(sections[0].id));
        setGradebookFocus(mode);
        setKpiHint('');
        return;
      }
      setKpiHint('Select a class in Class scope, then click On track or Needs help again.');
      return;
    }
    setKpiHint('');
    setGradebookFocus(mode);
  }

  return (
    <ThemeProvider theme={hubTheme}>
      <CssBaseline />
      {marketplaceOpen ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'auto' }}>
          <TeacherPackMarketplace
            enabledPackIds={enabledTrainingPacks}
            institutionName={displayInstitutionName || ''}
            backLabel={isTeacherHub ? 'Back to Teacher Dashboard' : 'Back to School Dashboard'}
            onBack={() => setMarketplaceOpen(false)}
          />
        </Box>
      ) : (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          background:
            'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(30, 60, 110, 0.55) 0%, rgba(6, 10, 22, 1) 45%, #040810 100%)'
        }}
      >
        <Box
          component="header"
          sx={{
            flexShrink: 0,
            zIndex: 10,
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderBottom: '1px solid rgba(120, 200, 255, 0.22)',
            bgcolor: 'rgba(6, 10, 22, 0.98)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
            <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}>
              {t('institutionHubOverline')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#e8f4ff' }}>
              {audience}
            </Typography>
            {displayInstitutionName ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                {displayInstitutionName}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
            {!previewUser && (isTeacherHub || role === 'admin' || role === 'tester') ? (
              <>
                <Tooltip title="CommandLine Marketplace — level packs & Skylite CTF boxes">
                  <IconButton
                    color="primary"
                    aria-label="Open CommandLine Marketplace"
                    onClick={() => setMarketplaceOpen(true)}
                  >
                    <ShoppingCartIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear resolved tickets">
                  <IconButton
                    color="primary"
                    aria-label="Clear resolved tickets"
                    onClick={(e) => setResolvedNoticesAnchor(e.currentTarget)}
                    sx={{
                      ...(resolvedNotices.length
                        ? {
                            animation: 'resolvedTicketPulse 1.4s ease-in-out infinite',
                            '@keyframes resolvedTicketPulse': {
                              '0%, 100%': {
                                transform: 'scale(1)',
                                boxShadow: '0 0 0 0 rgba(34, 211, 238, 0.55)'
                              },
                              '50%': {
                                transform: 'scale(1.12)',
                                boxShadow: '0 0 0 10px rgba(34, 211, 238, 0)'
                              }
                            }
                          }
                        : {})
                    }}
                  >
                    <Badge
                      badgeContent={resolvedNotices.length || null}
                      color="error"
                      overlap="circular"
                    >
                      <AssignmentTurnedInIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(resolvedNoticesAnchor)}
                  anchorEl={resolvedNoticesAnchor}
                  onClose={() => setResolvedNoticesAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      width: 360,
                      maxWidth: '92vw',
                      maxHeight: 420,
                      overflow: 'auto',
                      bgcolor: '#121c2f',
                      border: '1px solid rgba(120, 200, 255, 0.28)',
                      p: 1.5
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#e8f4ff' }}>
                    Student tickets resolved
                  </Typography>
                  {!resolvedNotices.length ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No resolved tickets waiting. The icon stops pulsing when this list is clear.
                    </Typography>
                  ) : (
                    <Stack spacing={1.25}>
                      {resolvedNotices.map((n) => (
                        <Paper
                          key={n.id}
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            bgcolor: 'rgba(8, 16, 32, 0.9)',
                            borderColor: 'rgba(120, 200, 255, 0.22)'
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#e8f4ff' }}>
                            {n.publicId || `Ticket #${n.ticketId}`}
                            {n.levelNumber != null ? ` · Level ${n.levelNumber}` : ''}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="skylite-privacy-blur"
                            data-skylite-privacy="blur"
                            sx={{ display: 'block', color: 'text.secondary' }}
                          >
                            {[n.studentName, n.studentEmail].filter(Boolean).join(' · ') || 'Student'}
                            {n.sectionName ? ` · ${n.sectionName}` : ''}
                            {n.meTooCount > 0 ? ` · +${n.meTooCount} me-too` : ''}
                          </Typography>
                          {n.bodyPreview ? (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                color: 'rgba(200,226,255,0.75)',
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              {String(n.bodyPreview).slice(0, 180)}
                              {String(n.bodyPreview).length > 180 ? '…' : ''}
                            </Typography>
                          ) : null}
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => dismissResolvedNotice(n.id)}
                            disabled={dismissingNoticeId === n.id}
                            sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
                          >
                            {dismissingNoticeId === n.id ? 'Clearing…' : 'Dismiss'}
                          </Button>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Popover>
              </>
            ) : null}
            {previewUser ? (
              <Chip
                icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
                label="UI preview · session stays on your owner login"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            ) : null}
            {previewUser && onExitSupportPreview ? (
              <Button
                variant="contained"
                color="primary"
                onClick={onExitSupportPreview}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                ← Admin Home
              </Button>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pb: 4 }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, sm: 3 }, pt: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {showSkyliteCard ? (
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <DesktopWindowsIcon sx={{ fontSize: 36, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {t('institutionCardAdvancedTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                      {t('institutionCardAdvancedBody')}
                    </Typography>
                    <Button variant="contained" onClick={onEnterSkylite} sx={{ textTransform: 'none', fontWeight: 800 }}>
                      {t('institutionCardAdvancedCta')}
                    </Button>
                  </Paper>
                </Grid>
              ) : null}
              <Grid item xs={12} md={showSkyliteCard ? 4 : 6}>
                <Paper sx={{ p: 2, height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <SchoolIcon sx={{ fontSize: 36, color: 'secondary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {t('institutionCardBasicTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                    {t('institutionCardBasicBody')}
                  </Typography>
                  <Button variant="outlined" href={trainingUrl} sx={{ textTransform: 'none', fontWeight: 800 }}>
                    {t('institutionCardBasicCta')}
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={showSkyliteCard ? 4 : 6}>
                <Paper
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    bgcolor: '#fffbeb',
                    border: '1px solid rgba(120, 200, 255, 0.22)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#78350f' }}>
                    {t('institutionLicenseExpirationLabel')}
                    {': '}
                    {licenseLoading
                      ? '…'
                      : licenseExpirationFormatted || t('institutionLicenseExpirationPending')}
                  </Typography>
                  {showRenewalPrompt ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e' }}>
                        {t('institutionLicenseExpireSoon')}
                      </Typography>
                      <Button
                        variant="contained"
                        color="warning"
                        disabled={renewalInquirySent || renewalBusy}
                        onClick={handleRenewalInquiry}
                        sx={{ textTransform: 'none', fontWeight: 800, alignSelf: 'flex-start' }}
                      >
                        {t('institutionLicenseRenewCta')}
                      </Button>
                      {renewalError ? (
                        <Typography variant="caption" sx={{ color: '#b45309' }}>
                          {renewalError}
                        </Typography>
                      ) : null}
                    </>
                  ) : null}
                </Paper>
              </Grid>
            </Grid>

            {kpiHint ? (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setKpiHint('')}>
                {kpiHint}
              </Alert>
            ) : null}

            {displayKpis.length ? (
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {displayKpis.map((k) => {
                  const clickable = k.id === 'on_track' || k.id === 'needs_help' || k.id === 'radar';
                  const cardSx = {
                    p: 2,
                    borderRadius: 2,
                    color: '#fff',
                    bgcolor: k.accent || '#1b4332',
                    minHeight: 110,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    textAlign: 'left'
                  };
                  const body = (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, pr: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.92, fontWeight: 700, flex: 1 }}>
                          {k.label}
                        </Typography>
                        {TEACHER_KPI_HELP[k.id] ? (
                          <HelpTip
                            title={TEACHER_KPI_HELP[k.id]}
                            label={`About ${k.label}`}
                            iconColor="rgba(255,255,255,0.85)"
                            size={15}
                          />
                        ) : null}
                      </Box>
                      <Typography variant="h4" sx={{ fontWeight: 900, my: 0.5 }}>
                        {k.value}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.88 }}>
                        {clickable ? `${k.trend || ''} · click for class detail`.trim() : k.trend}
                      </Typography>
                      {k.gauge ? <Gauge current={k.gauge.current} max={k.gauge.max} color="#fff" /> : null}
                      {k.spark ? <Sparkline values={k.spark} color="rgba(255,255,255,0.95)" /> : null}
                    </>
                  );
                  return (
                    <Grid item xs={12} sm={6} md={3} key={k.id}>
                      {clickable ? (
                        <ButtonBase
                          onClick={() => openGradebookFromKpi(k.id)}
                          focusRipple
                          sx={{
                            ...cardSx,
                            transition: 'transform 0.15s, box-shadow 0.15s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' }
                          }}
                        >
                          {body}
                        </ButtonBase>
                      ) : (
                        <Paper sx={cardSx}>{body}</Paper>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            ) : showLiveClassPanels ? (
              <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.5 }}>
                  No class analytics yet
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Create a class, share the join code, or add students by email. KPIs fill in from real Learning progress.
                </Typography>
              </Paper>
            ) : null}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} lg={canManageClasses ? 5 : 8}>
                <BarChartBlock data={showLiveClassPanels ? barChartData : baseMetrics.barChart} />
              </Grid>

              {canManageClasses ? (
                <Grid item xs={12} lg={4}>
                  <Paper
                    sx={{
                      p: 2,
                      height: '100%',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: { lg: 320 }
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75, color: '#e8f4ff', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      Student progress
                      <HelpTip title="Pathway completion from verified Learning progress for students in the selected class scope." />
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 1.25, color: '#94a3b8' }}>
                      {loadingClasses
                        ? 'Loading…'
                        : rosterStudents.length
                          ? 'Pathway completion from Learning progress.'
                          : 'No students in this scope yet.'}
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0, maxHeight: { xs: 280, lg: 360 }, overflowY: 'auto', pr: 0.5 }}>
                      {rosterStudents.map((row) => (
                        <TeacherStudentProgressRow
                          key={`${teacherSection}-${row.userId}`}
                          name={row.name}
                          email={row.email}
                          pct={row.pct}
                          onRemove={
                            selectedSection && !supportPreviewReadOnly
                              ? () => removeStudent(row.userId)
                              : undefined
                          }
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ) : null}

              <Grid item xs={12} lg={canManageClasses ? 3 : 4}>
                <Paper sx={{ p: 2, height: '100%', borderRadius: 2 }}>
                  {canManageClasses ? (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: '#e8f4ff', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        Class scope
                        <HelpTip title="Pick a class to filter KPIs, roster, pool adds, and the gradebook. “All” combines your classes for overview only — click On track / Needs help after selecting one class." />
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5 }}>
                        Same pattern as Schoology/Canvas: create a class, share a join code, or add by email. LMS launches also create/enroll automatically.
                      </Typography>
                      {supportPreviewReadOnly && !supportPreviewCanLoadLive ? (
                        <Typography variant="body2" sx={{ color: '#fbbf24' }}>
                          This preview has no school id — cannot load live classes.
                        </Typography>
                      ) : supportPreviewReadOnly ? (
                        <>
                          <Typography variant="body2" sx={{ color: '#fbbf24', mb: 1.5 }}>
                            Read-only support view of live classes. Create / add / archive stay with the teacher login.
                          </Typography>
                          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                            <InputLabel id="teacher-section-label-preview">Section</InputLabel>
                            <Select
                              labelId="teacher-section-label-preview"
                              label="Section"
                              value={teacherSection}
                              onChange={(e) => setTeacherSection(String(e.target.value))}
                              MenuProps={{
                                disablePortal: true,
                                PaperProps: { sx: { maxHeight: 320 } }
                              }}
                            >
                              <MenuItem value="all">All my students (combined)</MenuItem>
                              {sections.map((s) => (
                                <MenuItem key={s.id} value={String(s.id)}>
                                  {s.name} ({s.activeStudentCount || 0})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {selectedSection ? (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                                Join code
                                <HelpTip title="Share this code so students can join this class from CommandLine. LMS launches can also enroll automatically." size={14} />
                              </Typography>
                              <Typography variant="h6" className="notranslate" translate="no" sx={{ fontWeight: 900, letterSpacing: 2 }}>
                                {selectedSection.joinCode || '—'}
                              </Typography>
                            </Box>
                          ) : null}
                          {classError ? (
                            <Typography variant="caption" sx={{ color: '#fb923c', display: 'block', mt: 1 }}>
                              {classError}
                            </Typography>
                          ) : null}
                        </>
                      ) : (
                        <>
                          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                            <InputLabel id="teacher-section-label">Section</InputLabel>
                            <Select
                              labelId="teacher-section-label"
                              label="Section"
                              value={teacherSection}
                              onChange={(e) => setTeacherSection(String(e.target.value))}
                              MenuProps={{
                                disablePortal: true,
                                PaperProps: { sx: { maxHeight: 320 } }
                              }}
                            >
                              <MenuItem value="all">All my students (combined)</MenuItem>
                              {sections.map((s) => (
                                <MenuItem key={s.id} value={String(s.id)}>
                                  {s.name} ({s.activeStudentCount || 0})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {selectedSection ? (
                            <Box sx={{ mb: 1.5 }}>
                              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
                                Join code
                                <HelpTip title="Share this code so students can join this class from CommandLine. LMS launches can also enroll automatically." size={14} />
                              </Typography>
                              <Typography variant="h6" className="notranslate" translate="no" sx={{ fontWeight: 900, letterSpacing: 2 }}>
                                {selectedSection.joinCode || '—'}
                              </Typography>
                              <Button
                                size="small"
                                color="inherit"
                                onClick={archiveSection}
                                disabled={busy}
                                sx={{ textTransform: 'none', mt: 0.5 }}
                              >
                                Archive class
                              </Button>
                            </Box>
                          ) : null}

                          <TextField
                            size="small"
                            fullWidth
                            label="New class name"
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={createSection}
                            disabled={busy || !newSectionName.trim()}
                            sx={{ textTransform: 'none', fontWeight: 800, mb: 1.5 }}
                          >
                            Create class
                          </Button>

                          {selectedSection ? (
                            <>
                              <TextField
                                size="small"
                                fullWidth
                                label="Add student by email"
                                value={addEmail}
                                onChange={(e) => setAddEmail(e.target.value)}
                                sx={{ mb: 1 }}
                              />
                              <Button
                                fullWidth
                                variant="outlined"
                                onClick={addStudentByEmail}
                                disabled={busy || !addEmail.trim()}
                                sx={{ textTransform: 'none', fontWeight: 700, mb: 1.5 }}
                              >
                                Add to this class
                              </Button>
                              {unassigned.length ? (
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.35, mb: 0.75 }}
                                  >
                                    Your licensed students not in a class yet
                                    <HelpTip title="Only students who redeemed seats from your license batches appear here — not every student at the school." />
                                  </Typography>
                                  <Box sx={{ maxHeight: 140, overflowY: 'auto' }}>
                                    {unassigned.slice(0, 40).map((s) => (
                                      <Box
                                        key={s.userId}
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          gap: 1,
                                          mb: 0.5
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          className="skylite-privacy-blur"
                                          data-skylite-privacy="blur"
                                          sx={{ color: '#e8f4ff', minWidth: 0 }}
                                        >
                                          {s.name || s.email}
                                        </Typography>
                                        <Button
                                          size="small"
                                          onClick={() => addFromPool(s.userId)}
                                          disabled={busy}
                                          sx={{ textTransform: 'none', minWidth: 0 }}
                                        >
                                          Add
                                        </Button>
                                      </Box>
                                    ))}
                                  </Box>
                                </Box>
                              ) : null}
                            </>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                              Select a class to add students or copy its join code.
                            </Typography>
                          )}
                          {classError ? (
                            <Typography variant="caption" sx={{ color: '#fb923c', display: 'block', mt: 1 }}>
                              {classError}
                            </Typography>
                          ) : null}
                        </>
                      )}
                    </>
                  ) : isDistrictLeaderHub ? (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: '#e8f4ff' }}>
                        Schools overview
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        Campus roll-ups will appear here from live memberships and class enrollments — no sample schools.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: '#e8f4ff' }}>
                        {isStudentHub ? 'Your workspace' : 'Institution summary'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                        {isStudentHub
                          ? 'Ask your teacher for a class join code, or launch from your LMS course to join automatically.'
                          : 'Manage classes, student progress, and training from this hub.'}
                      </Typography>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {canManageClasses && !supportPreviewReadOnly ? (
              <Box sx={{ mt: 2, mb: 2 }}>
                <TeacherGradebookPanel
                  sectionId={teacherSection}
                  sectionName={selectedSection?.name}
                  sections={sections}
                  disabled={busy}
                  focusMode={gradebookFocus}
                  onFocusHandled={() => setGradebookFocus(null)}
                  panelRef={gradebookPanelRef}
                  onReassigned={() => refreshClasses()}
                  canImpersonateStudents={canImpersonateStudents}
                />
              </Box>
            ) : null}

            <Paper sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #0d9488' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 1 }}>
                {baseMetrics.forecast.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.7 }}>
                {baseMetrics.forecast.body}
              </Typography>
            </Paper>

            {baseMetrics.lmsPlaceholder ? (
              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  border: '1px dashed rgba(148,163,184,0.45)',
                  bgcolor: 'rgba(18, 28, 47, 0.85)'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.75 }}>
                  {baseMetrics.lmsPlaceholder.title}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
                  {baseMetrics.lmsPlaceholder.body}
                </Typography>
              </Paper>
            ) : null}
          </Box>
        </Box>
      </Box>
      )}
    </ThemeProvider>
  );
}
